import type { ReactNode } from 'react';
import { useDropzone, type Accept } from 'react-dropzone';
import { UploadCloud } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DropzoneProps {
  /** Called with the dropped/selected files. The caller owns the upload action. */
  onFiles: (files: File[]) => void;
  accept?: Accept;
  multiple?: boolean;
  disabled?: boolean;
  className?: string;
  /** Custom inner content; defaults to an upload icon + label. */
  children?: ReactNode;
  /** Label shown by the default content when idle. */
  label?: string;
}

/**
 * Dropzone — the canonical "box" upload affordance: a dashed, clickable area that
 * accepts drag-and-drop or click-to-select and hands the files to `onFiles`. The
 * caller owns the upload behaviour (DMS, Drive+DMS, attach, …); this is presentation
 * only, so every feature's upload box looks and behaves the same. Built on
 * react-dropzone. For "drag anywhere onto a list" use the overlay pattern instead.
 */
export function Dropzone({
  onFiles,
  accept,
  multiple = true,
  disabled = false,
  className,
  children,
  label = 'Drop files to upload, or click to select',
}: DropzoneProps) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (accepted) => {
      if (accepted.length > 0) onFiles(accepted);
    },
    accept,
    multiple,
    disabled,
  });

  return (
    <div
      {...getRootProps()}
      className={cn(
        'flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-4 text-center transition-colors',
        disabled
          ? 'cursor-not-allowed opacity-60'
          : 'cursor-pointer hover:border-primary/50 hover:bg-muted/50',
        isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25',
        className,
      )}
    >
      <input {...getInputProps()} />
      {children ?? (
        <div className="flex items-center justify-center gap-2">
          <UploadCloud className="h-5 w-5 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{isDragActive ? 'Drop files here…' : label}</p>
        </div>
      )}
    </div>
  );
}
